import React, { useState, useEffect, useCallback } from 'react'
import { Clock, Eye, EyeOff, Monitor, MonitorOff } from 'lucide-react'
import { Slide } from './Slide'
import type { SlideData, PresenterTimerState } from '../../types/presentation'

export interface PresenterModeProps {
  slides: SlideData[]
  currentSlide: number
  onNextSlide: () => void
  onPreviousSlide: () => void
  onClose: () => void
}

export const PresenterMode: React.FC<PresenterModeProps> = ({
  slides,
  currentSlide,
  onNextSlide,
  onPreviousSlide,
  onClose,
}) => {
  const [showNotes, setShowNotes] = useState(true)
  const [showNextSlide, setShowNextSlide] = useState(true)
  const [showTimer, setShowTimer] = useState(true)
  const [timer, setTimer] = useState<PresenterTimerState>({
    elapsed: 0,
    isRunning: true,
  })

  // Current slide and next slide
  const currentSlideData = slides[currentSlide]
  const nextSlideData = slides[currentSlide + 1]
  const presenterNotes = currentSlideData?.notes || ''

  // Timer functionality
  useEffect(() => {
    if (!timer.isRunning) return

    const interval = setInterval(() => {
      setTimer((prev) => ({
        ...prev,
        elapsed: prev.elapsed + 1,
      }))
    }, 1000)

    return () => clearInterval(interval)
  }, [timer.isRunning])

  // Format elapsed time as MM:SS
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Toggle timer
  const toggleTimer = useCallback(() => {
    setTimer((prev) => ({ ...prev, isRunning: !prev.isRunning }))
  }, [])

  // Reset timer
  const resetTimer = useCallback(() => {
    setTimer({ elapsed: 0, isRunning: timer.isRunning })
  }, [timer.isRunning])

  // Calculate remaining time based on slide durations
  const calculateTotalTime = useCallback(() => {
    let total = 0
    for (let i = currentSlide; i < slides.length; i++) {
      total += slides[i].duration || 60 // Default 60 seconds per slide
    }
    return total
  }, [slides, currentSlide])

  const remainingTime = calculateTotalTime() - timer.elapsed

  // Keyboard shortcuts for presenter mode
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Toggle notes with N
      if (e.key.toLowerCase() === 'n') {
        e.preventDefault()
        setShowNotes((prev) => !prev)
        return
      }

      // Toggle next slide preview with X
      if (e.key.toLowerCase() === 'x') {
        e.preventDefault()
        setShowNextSlide((prev) => !prev)
        return
      }

      // Toggle timer with C
      if (e.key.toLowerCase() === 'c') {
        e.preventDefault()
        setShowTimer((prev) => !prev)
        return
      }

      // Pause/resume timer with R
      if (e.key.toLowerCase() === 'r') {
        e.preventDefault()
        toggleTimer()
        return
      }

      // Reset timer with Shift+R
      if (e.key.toLowerCase() === 'r' && e.shiftKey) {
        e.preventDefault()
        resetTimer()
        return
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [toggleTimer, resetTimer])

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 text-white flex">
      {/* Main Presenter View */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar - Timer and Slide Counter */}
        <div className="h-16 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-6">
          {/* Left: Timer */}
          {showTimer && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-700 px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5 text-blue-400" />
                <div className="flex flex-col">
                  <span className="text-2xl font-bold font-mono">
                    {formatTime(timer.elapsed)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {timer.isRunning ? 'Running' : 'Paused'}
                  </span>
                </div>
                <button
                  onClick={toggleTimer}
                  className="ml-2 p-1 hover:bg-gray-600 rounded transition-colors"
                  title={timer.isRunning ? 'Pause timer (R)' : 'Start timer (R)'}
                >
                  {timer.isRunning ? (
                    <MonitorOff className="w-4 h-4 text-yellow-400" />
                  ) : (
                    <Monitor className="w-4 h-4 text-green-400" />
                  )}
                </button>
                <button
                  onClick={resetTimer}
                  className="p-1 hover:bg-gray-600 rounded transition-colors"
                  title="Reset timer (Shift+R)"
                >
                  <span className="text-xs text-gray-400">↺</span>
                </button>
              </div>

              {/* Remaining Time */}
              <div className="flex items-center gap-2 bg-gray-700 px-4 py-2 rounded-lg">
                <span className="text-sm text-gray-400">Remaining:</span>
                <span className="text-lg font-bold font-mono text-orange-400">
                  {formatTime(Math.max(0, remainingTime))}
                </span>
              </div>

              {/* Current Slide / Total */}
              <div className="bg-gray-700 px-4 py-2 rounded-lg">
                <span className="text-lg font-bold">
                  {currentSlide + 1} / {slides.length}
                </span>
              </div>
            </div>
          )}

          {/* Right: Close Button */}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors font-medium"
            title="Exit presenter mode (Escape)"
          >
            Exit Presenter Mode
          </button>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Current Slide */}
          <div className="flex-1 p-6 flex items-center justify-center bg-black">
            <div className="w-full h-full flex items-center justify-center">
              <Slide
                data={currentSlideData}
                isActive={true}
                transition="none"
                direction="forward"
              />
            </div>
          </div>

          {/* Right Panel - Notes and Next Slide */}
          <div className="w-96 bg-gray-800 border-l border-gray-700 flex flex-col">
            {/* Presenter Notes */}
            {showNotes && presenterNotes && (
              <div className="flex-1 p-6 border-b border-gray-700 overflow-y-auto">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="text-blue-400">📝</span>
                  Presenter Notes
                </h3>
                <div className="prose prose-invert prose-sm max-w-none">
                  <p className="text-gray-300 whitespace-pre-wrap">{presenterNotes}</p>
                </div>
              </div>
            )}

            {/* Next Slide Preview */}
            {showNextSlide && nextSlideData && (
              <div className="h-1/2 p-6 overflow-y-auto">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="text-green-400">➜</span>
                  Next Slide
                </h3>
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="aspect-video flex items-center justify-center">
                    <Slide
                      data={nextSlideData}
                      isActive={false}
                      transition="none"
                      direction="forward"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* No Next Slide */}
            {showNextSlide && !nextSlideData && (
              <div className="h-1/2 p-6 flex items-center justify-center">
                <p className="text-gray-400 text-center">
                  This is the last slide.
                  <br />
                  <span className="text-sm">Press Escape to exit presenter mode.</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar - Navigation and Toggle Controls */}
        <div className="h-16 bg-gray-800 border-t border-gray-700 flex items-center justify-between px-6">
          {/* Left: Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={onPreviousSlide}
              disabled={currentSlide === 0}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 rounded-lg transition-colors font-medium"
            >
              Previous (←)
            </button>
            <button
              onClick={onNextSlide}
              disabled={currentSlide === slides.length - 1}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-800 disabled:text-gray-500 rounded-lg transition-colors font-medium"
            >
              Next (→ / Space)
            </button>
          </div>

          {/* Right: Toggle Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNotes((prev) => !prev)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm font-medium"
              title="Toggle notes (N)"
            >
              {showNotes ? <Eye className="w-4 h-4 inline mr-1" /> : <EyeOff className="w-4 h-4 inline mr-1" />}
              Notes
            </button>
            <button
              onClick={() => setShowNextSlide((prev) => !prev)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm font-medium"
              title="Toggle next slide (X)"
            >
              {showNextSlide ? <Eye className="w-4 h-4 inline mr-1" /> : <EyeOff className="w-4 h-4 inline mr-1" />}
              Next Slide
            </button>
            <button
              onClick={() => setShowTimer((prev) => !prev)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm font-medium"
              title="Toggle timer (C)"
            >
              {showTimer ? <Eye className="w-4 h-4 inline mr-1" /> : <EyeOff className="w-4 h-4 inline mr-1" />}
              Timer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
